import { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import {
  addReaction,
  calculateSeenMembers,
} from "../../../../services/chats/frontend-chats";
import { GetAuthUserLocalStorage } from "../../../../services/localStorage/localStorage";
import { useDispatch, useSelector } from "react-redux";
import { REACTION_ORDER } from "../../../../utils/reactions";

const ReactionPicker = ({
  isUserNearBottomRef,
  scrollToBottom,
  visible,
  onClose,
  message,
  chatId,
  onDeleteMessage,
  onEditMessage,
  onSeenBy,
  isLastMessage = false,
}) => {
  const dispatch = useDispatch();
  const authUser = GetAuthUserLocalStorage();
  const pickerRef = useRef(null);
  const isMessageOwner = message?.sender_id === authUser?.id;
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isMouseOverMenu, setIsMouseOverMenu] = useState(false);

  // Get chat data from Redux store to calculate seen members
  const { selectedUser } = useSelector((state) => state?.chats);

  // Calculate seen members count
  const seenMembers = calculateSeenMembers(message, selectedUser);
  const seenCount = seenMembers.length;

  // Determine if there is anything to show in the dropdown
  const hasMenu = Boolean(selectedUser?.is_group_chat || isMessageOwner);

  const handleReactionSelect = async (emoji) => {
    if (!message || !chatId || !authUser?.id) return;

    try {
      // Only scroll to bottom if this is the last message in the list
      if (isLastMessage) {
        scrollToBottom();
      }
      await addReaction(
        message.thread_id,
        chatId,
        emoji,
        authUser.id,
        dispatch,
        message,
      );
      onClose();
    } catch (error) {
      console.error("Error adding reaction:", error);
    }
  };

  const handleDeleteMessage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDeleteMessage && isMessageOwner) {
      onDeleteMessage(message);
      onClose();
    }
  };

  const handleSeenBy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSeenBy) {
      onSeenBy(message);
    }
    setShowMoreMenu(false);
  };

  const handleEditMessage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEditMessage) {
      onEditMessage(message);
    }
    setShowMoreMenu(false);
    onClose();
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        // Add a small delay if mouse is over menu to prevent accidental closing
        if (isMouseOverMenu) {
          setTimeout(() => {
            if (!isMouseOverMenu) {
              onClose();
              setShowMoreMenu(false);
            }
          }, 100);
        } else {
          onClose();
          setShowMoreMenu(false);
        }
      }
    };

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [visible, onClose, isMouseOverMenu]);

  if (!visible || !message) return null;

  return (
    <div
      ref={pickerRef}
      className="reaction-picker-floating"
      style={{
        position: "absolute",
        top: "0px",
        right: "5px",
        zIndex: 10000,
      }}
    >
      <div className="reaction-picker-content">
        <div
          className="reaction-options"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            minWidth: "120px",
            justifyContent: "flex-start",
          }}
        >
          {REACTION_ORDER.map((emoji) => (
            <div
              key={emoji}
              className="reaction-option"
              onClick={() => handleReactionSelect(emoji)}
            >
              <span className="reaction-emoji">{emoji}</span>
            </div>
          ))}

          {/* More button with Bootstrap Dropdown - only render if there are any items */}
          {hasMenu && (
            <Dropdown
              show={showMoreMenu}
              onToggle={setShowMoreMenu}
              placement="bottom-end"
              flip={false}
            >
              <Dropdown.Toggle
                as="div"
                style={{
                  color: "#6c757d",
                  fontSize: "13px",
                  fontWeight: "500",
                  padding: "4px 8px",
                  cursor: "pointer",
                  borderRadius: "4px",
                  transition: "background-color 0.2s",
                  whiteSpace: "nowrap",
                  marginLeft: "4px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: showMoreMenu ? "#f8f9fa" : "transparent",
                  border: "none",
                  outline: "none",
                }}
                variant="link"
                className="no-arrow"
              >
                More...
              </Dropdown.Toggle>

              <Dropdown.Menu
                style={{
                  borderRadius: "6px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  minWidth: "140px",
                  padding: "4px 4px",
                  marginTop: "2px",
                  marginBottom: "5px",
                }}
                onMouseEnter={() => setIsMouseOverMenu(true)}
                onMouseLeave={() => setIsMouseOverMenu(false)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => e.stopPropagation()}
              >
                {selectedUser?.is_group_chat && (
                  <Dropdown.Item
                    onClick={handleSeenBy}
                    style={{
                      fontSize: "13px",
                      color: "#495057",
                      padding: "8px 12px",
                    }}
                  >
                    Seen By ({seenCount})
                  </Dropdown.Item>
                )}

                {isMessageOwner && (
                  <Dropdown.Item
                    onClick={handleEditMessage}
                    style={{
                      fontSize: "13px",
                      color: "#495057",
                      padding: "8px 12px",
                    }}
                  >
                    Edit Message
                  </Dropdown.Item>
                )}

                {isMessageOwner && (
                  <Dropdown.Item
                    onClick={handleDeleteMessage}
                    style={{
                      fontSize: "13px",
                      color: "#dc3545",
                      padding: "8px 12px",
                    }}
                  >
                    Delete
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReactionPicker;
